import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ProtectedRoute, AdminRoute } from '@/utils/routeProtection';
import { lazyImport } from '@/utils/lazyImport';
import HomePageRedirect from '@/components/HomePageRedirect';
import DevelopmentPlansGuard from '@/components/leadership/DevelopmentPlansGuard';
import FohFoodSafetyGate from '@/components/FohFoodSafetyGate';
import EquipmentPageGate from '@/components/EquipmentPageGate';
import DirectorBoardGate from '@/components/DirectorBoardGate';

// Paths where we want to preserve scroll position (e.g. after task completion)
const PRESERVE_SCROLL_PATHS = [
  '/kitchen/checklists',
];

function ScrollToTop() {
  const { pathname } = useLocation();
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    const isTrainingNavigation =
      previousPathnameRef.current.startsWith('/training') &&
      pathname.startsWith('/training');
    const shouldPreserve =
      isTrainingNavigation ||
      PRESERVE_SCROLL_PATHS.some(p => pathname.includes(p));

    if (!shouldPreserve) {
      window.scrollTo(0, 0);
      // On mobile — and always in the installed PWA, where #root is
      // `overflow: hidden` and `body` is `position: fixed` — the document does
      // not scroll: `.mobile-main` does, and it is not remounted across routes.
      // So `window.scrollTo` is a silent no-op there and a new page opened
      // already scrolled to the previous page's offset, often past its end.
      document.querySelector('.mobile-main')?.scrollTo(0, 0);
    }

    previousPathnameRef.current = pathname;
  }, [pathname]);

  return null;
}

// Keep critical auth pages as regular imports for faster initial load
import Login from '../pages/Login';
import Register from '../pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';

// Lazy load all other pages to reduce initial bundle size
const Dashboard = lazyImport(() => import('../pages/Dashboard/Dashboard'));

// Evaluations - Heavy due to forms and charts
const Evaluations = lazyImport(() => import('../pages/Evaluations/index'));
const ScheduledEvaluations = lazyImport(() => import('../pages/Evaluations/ScheduledEvaluations'));
const NewEvaluation = lazyImport(() => import('../pages/Evaluations/NewEvaluation'));
const EnhancedEvaluationForm = lazyImport(() => import('../pages/Evaluations/EnhancedEvaluationForm'));
const ViewEvaluation = lazyImport(() => import('@/pages/Evaluations/ViewEvaluation'));
const LeaderReviewsToDo = lazyImport(() => import('@/pages/Evaluations/LeaderReviewsToDo'));
const LeaderReview = lazyImport(() => import('@/pages/Evaluations/LeaderReview'));

// Templates
const Templates = lazyImport(() => import('@/pages/Templates/index'));
const TemplateBuilder = lazyImport(() => import('../pages/Templates/TemplateBuilder'));

// Settings and Users
const Settings = lazyImport(() => import('../pages/Settings/index'));
const Users = lazyImport(() => import('../pages/users/index'));
const UserProfile = lazyImport(() => import('../pages/users/[id]/index'));
const EditUser = lazyImport(() => import('../pages/users/[id]/edit'));

// Rewards
const Rewards = lazyImport(() => import('../pages/Rewards/index'));
const RewardsManagement = lazyImport(() => import('../pages/Admin/RewardsManagement'));

// Analytics - Very heavy due to charts and data processing
const AnalyticsHub = lazyImport(() => import('@/pages/Analytics'));
const HeartsAndHands = lazyImport(() => import('@/pages/Analytics/HeartsAndHands'));
const DayVsNight = lazyImport(() => import('@/pages/Analytics/DayVsNight'));
const EvaluationTrends = lazyImport(() => import('@/pages/Analytics/EvaluationTrends'));
const DepartmentComparison = lazyImport(() => import('@/pages/Analytics/DepartmentComparison'));
const TeamScores = lazyImport(() => import('@/pages/Analytics/TeamScores'));
const PerformanceHeatmap = lazyImport(() => import('@/pages/Analytics/PerformanceHeatmap'));

// Documentation
const DocumentationPage = lazyImport(() => import('@/pages/Documentation'));
const NewDocument = lazyImport(() => import('@/pages/Documentation/NewDocument'));
const DocumentDetail = lazyImport(() => import('@/pages/Documentation/[id]'));
const EditDocument = lazyImport(() => import('@/pages/Documentation/[id]/edit'));
const DisciplineAnalytics = lazyImport(() => import('@/pages/Documentation/DisciplineAnalytics'));

// Kitchen - Heavy due to complex forms and data
const Kitchen = lazyImport(() => import('@/pages/Kitchen'));
const FoodSafety = lazyImport(() => import('../pages/Kitchen/FoodSafety'));
const CompleteChecklist = lazyImport(() => import('../pages/Kitchen/FoodSafety/CompleteChecklist'));
const DailyChecklist = lazyImport(() => import('../pages/Kitchen/FoodSafety/DailyChecklist'));
const ChecklistHistory = lazyImport(() => import('../pages/Kitchen/FoodSafety/components/ChecklistHistory'));
const History = lazyImport(() => import('../pages/Kitchen/FoodSafety/pages/History/index'));
const ChecklistAnalytics = lazyImport(() => import('../pages/Kitchen/FoodSafety/components/ChecklistAnalytics'));
const ViewCompletion = lazyImport(() => import('../pages/Kitchen/FoodSafety/ViewCompletion'));
const KitchenHome = lazyImport(() => import('@/pages/Kitchen/Home'));

// Leadership - Heavy due to complex features
const Leadership = lazyImport(() => import('@/pages/Leadership'));
const LeadershipDashboard = lazyImport(() => import('@/pages/Leadership/Dashboard'));

// Leadership pages - Heavy due to complex features and forms
const DevelopmentPage = lazyImport(() => import('@/pages/Leadership/DevelopmentPage'));
const PlanTasks = lazyImport(() => import('@/pages/Leadership/PlanTasks'));
const PersonalPlanPage = lazyImport(() => import('@/pages/Leadership/PersonalPlan/PersonalPlanPage'));
const TrainingPrograms = lazyImport(() => import('@/pages/Leadership/TrainingPrograms'));
const TrainingProgramDetails = lazyImport(() => import('@/pages/Leadership/TrainingProgramDetails'));
const Assessments = lazyImport(() => import('@/pages/Leadership/Assessments'));
const AssessmentDetails = lazyImport(() => import('@/pages/Leadership/AssessmentDetails'));
const AssessmentTaking = lazyImport(() => import('@/pages/Leadership/AssessmentTaking'));
const AssessmentResults = lazyImport(() => import('@/pages/Leadership/AssessmentResults'));
const LeadershipStyleResults = lazyImport(() => import('@/pages/Leadership/LeadershipStyleResults'));
const Subscription = lazyImport(() => import('@/pages/Leadership/Subscription'));

// 360 Evaluations - Heavy due to complex forms
const Evaluations360 = lazyImport(() => import('@/pages/Leadership/Evaluations360'));
const New360Evaluation = lazyImport(() => import('@/pages/Leadership/Evaluations360/New360Evaluation'));
const AddEvaluators = lazyImport(() => import('@/pages/Leadership/Evaluations360/AddEvaluators'));
const View360Evaluation = lazyImport(() => import('@/pages/Leadership/Evaluations360/View360Evaluation'));
const Evaluators = lazyImport(() => import('@/pages/Leadership/Evaluations360/Evaluators'));
const DevelopmentPlan = lazyImport(() => import('@/pages/Leadership/Evaluations360/DevelopmentPlan'));

// Other leadership features
const LeadershipSettings = lazyImport(() => import('@/pages/Leadership/Settings'));
const MyLeaders = lazyImport(() => import('@/pages/Leadership/MyLeaders'));
const OneOnOnePage = lazyImport(() => import('@/pages/Leadership/OneOnOnePage'));
const LeadershipCalendar = lazyImport(() => import('@/pages/Leadership/Calendar'));
const LeadershipDevelopment = lazyImport(() => import('@/pages/Leadership/LeadershipDevelopment'));

// Team Surveys - Heavy due to complex forms and data processing
const TeamSurveys = lazyImport(() => import('@/pages/TeamSurveys'));
const AllTeamSurveys = lazyImport(() => import('@/pages/TeamSurveys/AllSurveys'));
const TeamSurveysDashboard = lazyImport(() => import('@/pages/TeamSurveys/Dashboard'));
const NewTeamSurvey = lazyImport(() => import('@/pages/TeamSurveys/NewSurvey'));
const CreateAdvanced = lazyImport(() => import('@/pages/TeamSurveys/CreateAdvanced'));
const TeamSurveyBuilder = lazyImport(() => import('@/pages/TeamSurveys/SurveyBuilder'));
const TeamSurveyResults = lazyImport(() => import('@/pages/TeamSurveys/SurveyResults'));
const TeamSurveyAnalytics = lazyImport(() => import('@/pages/TeamSurveys/Analytics'));
const SurveyTemplates = lazyImport(() => import('@/pages/TeamSurveys/Templates'));
const TakeSurvey = lazyImport(() => import('@/pages/TeamSurveys/TakeSurvey'));

// Team Development
const TeamDevelopment = lazyImport(() => import('@/pages/TeamDevelopment'));
const TeamMyPlans = lazyImport(() => import('@/pages/TeamDevelopment/MyPlans'));
const TeamOverview = lazyImport(() => import('@/pages/TeamDevelopment/TeamOverview'));
const TeamDevelopmentRedirect = lazyImport(() => import('@/components/TeamDevelopmentRedirect'));

// Kitchen additional pages
const Checklists = lazyImport(() => import('@/pages/Kitchen/Checklists'));
const KitchenChecklistHistory = lazyImport(() => import('@/pages/Kitchen/Checklists/History'));
const WasteTracker = lazyImport(() => import('@/pages/Kitchen/WasteTracker'));

// Kitchen Equipment and Cleaning
const Equipment = lazyImport(() => import('@/pages/Kitchen/Equipment'));
const EquipmentHistory = lazyImport(() => import('@/pages/Kitchen/Equipment/History/Overview'));
const EquipmentHistoryDetail = lazyImport(() => import('@/pages/Kitchen/Equipment/History/Detail'));

const WasteAnalytics = lazyImport(() => import('@/pages/Kitchen/WasteTracker/Analytics'));
const KitchenCleaning = lazyImport(() => import('@/pages/Kitchen/Cleaning'));
const KitchenCleaningHistory = lazyImport(() => import('@/pages/Kitchen/Cleaning/CleaningHistoryPage'));

// Safe Counting
const SafeCounting = lazyImport(() => import('@/pages/SafeCounting'));

// Training - Heavy due to complex features and assessments
const Training = lazyImport(() => import('@/pages/Training'));
const TrainingProgress = lazyImport(() => import('@/pages/Training/Progress/TrainingProgress'));
const TrainingPlanList = lazyImport(() => import('@/pages/Training/PlanList'));
const NewHires = lazyImport(() => import('@/pages/Training/NewHires'));
const TrainingDetails = lazyImport(() => import('@/pages/Training/Progress/TrainingDetails'));
const PlanDetails = lazyImport(() => import('@/pages/Training/PlanDetails'));
const CommunityPlans = lazyImport(() => import('@/pages/Training/CommunityPlans'));
const CommunityPlanDetails = lazyImport(() => import('@/pages/Training/CommunityPlanDetails'));

// Training Assessments - Heavy due to complex forms
const TrainingAssessments = lazyImport(() => import('@/pages/Training/Assessments'));
const CreateAssessment = lazyImport(() => import('@/pages/Training/Assessments/CreateAssessment'));
const TakeAssessment = lazyImport(() => import('@/pages/Training/Assessments/TakeAssessment'));
const DevelopmentPlanBuilder = lazyImport(() => import('@/pages/Training/DevelopmentPlans/DevelopmentPlanBuilder'));
const SendAssessment = lazyImport(() => import('@/pages/Training/Assessments/SendAssessment'));
const ConductAssessment = lazyImport(() => import('@/pages/Training/Assessments/ConductAssessment'));
const ReviewAssessment = lazyImport(() => import('@/pages/Training/Assessments/ReviewAssessment'));
const TrainingAssessmentResults = lazyImport(() => import('@/pages/Training/Assessments/AssessmentResults'));
const AssessmentPreview = lazyImport(() => import('@/pages/Training/Assessments/AssessmentPreview'));

// Miscellaneous pages
const FuturePage = lazyImport(() => import('@/pages/FuturePage'));
const AssignManagers = lazyImport(() => import('../pages/users/AssignManagers'));
const DailyChecklistHistory = lazyImport(() => import('../pages/Kitchen/FoodSafety/DailyChecklistHistory'));
const TemperatureHistory = lazyImport(() => import('../pages/Kitchen/FoodSafety/pages/TemperatureHistory'));

// Landing and FOH
const LandingPage = lazyImport(() => import('../pages/Landing'));
const FOH = lazyImport(() => import('../pages/FOH'));
const FOHLayout = lazyImport(() => import('../pages/FOH/FOHLayout'));
const FOHHistory = lazyImport(() => import('../pages/FOH/History'));
const FohEquipmentPage = lazyImport(() => import('../pages/FOH/Equipment/FohEquipmentPage'));
const DirectorBoardPage = lazyImport(() => import('../pages/DirectorBoard/DirectorBoardPage'));
const DirectorHistory = lazyImport(() => import('../pages/DirectorBoard/History'));
const CleaningPage = lazyImport(() => import('../pages/Cleaning/CleaningPage'));
const CleaningHistory = lazyImport(() => import('../pages/Cleaning/CleaningHistory'));

// Setup Sheets - Heavy due to complex forms
const SetupSheetBuilder = lazyImport(() => import('@/pages/SetupSheetBuilder').then(m => ({ default: m.SetupSheetBuilder })));
const SetupSheetTemplates = lazyImport(() => import('@/pages/SetupSheetTemplates').then(m => ({ default: m.SetupSheetTemplates })));
const SavedSetups = lazyImport(() => import('@/pages/SavedSetups').then(m => ({ default: m.SavedSetups })));
const ShiftSummary = lazyImport(() => import('@/pages/ShiftSummary').then(m => ({ default: m.ShiftSummary })));
const ShiftSummaryHistory = lazyImport(() => import('@/pages/ShiftSummaryHistory'));
const SetupViewLoader = lazyImport(() => import('@/pages/SetupView/SetupViewLoader').then(m => ({ default: m.SetupViewLoader })));
const PrintableSetupView = lazyImport(() => import('@/pages/SetupView/PrintableSetupView').then(m => ({ default: m.PrintableSetupView })));
const EditTemplate = lazyImport(() => import('@/pages/EditTemplate').then(m => ({ default: m.EditTemplate })));
const SetupTestView = lazyImport(() => import('@/pages/SetupTestView'));
// Admin and other features
const InvoicesPage = lazyImport(() => import('@/pages/Invoices'));
const StoresPage = lazyImport(() => import('@/pages/Admin/Stores'));
const AdminLayout = lazyImport(() => import('@/pages/Admin/AdminLayout'));
const AdminDashboard = lazyImport(() => import('@/pages/Admin/Dashboard'));
const AdminMessages = lazyImport(() => import('@/pages/Admin/Messages'));
const HealthDashboardPage = lazyImport(() => import('@/components/HealthDashboard').then(module => ({ default: module.HealthDashboard })));
const SubscriptionManagementPage = lazyImport(() => import('@/pages/Admin/SubscriptionManagement'));
const ProspectsPage = lazyImport(() => import('@/pages/Admin/Prospects'));
const AccountsPage = lazyImport(() => import('@/pages/Admin/Accounts'));
const OutreachPage = lazyImport(() => import('@/pages/Admin/Outreach'));
const FirstLoginPage = lazyImport(() => import('@/pages/Admin/FirstLogin'));
const Messages = lazyImport(() => import('@/pages/Messages/Messages'));
const Chat = lazyImport(() => import('@/pages/Chat'));
const Vendors = lazyImport(() => import('@/pages/Vendors'));
const QuickLinks = lazyImport(() => import('@/pages/QuickLinks'));
const BorrowLogPage = lazyImport(() => import('@/pages/BorrowLog'));
const ShiftReportsPage = lazyImport(() => import('@/pages/ShiftReports'));
const LateTimeOffPage = lazyImport(() => import('@/pages/LateTimeOff'));
const ServSafePage = lazyImport(() => import('@/pages/ServSafe'));
const ServSafeInspectorPage = lazyImport(() => import('@/pages/ServSafe/InspectorView'));

// Group meetings — no requiredFeature: the meeting rhythm works for every store.
const MeetingsList = lazyImport(() => import('@/pages/Meetings/MeetingsList'));
const NewMeeting = lazyImport(() => import('@/pages/Meetings/NewMeeting'));
const MeetingDetail = lazyImport(() => import('@/pages/Meetings/MeetingDetail'));
const SelfAssessmentsPage = lazyImport(() => import('@/pages/SelfAssessments'));
const SelfAssessmentReportPage = lazyImport(() => import('@/pages/SelfAssessments/ReportForm'));
const HowTo = lazyImport(() => import('@/pages/HowTo'));
const NotificationsPage = lazyImport(() => import('@/pages/Notifications'));
const AndroidNotificationTest = lazyImport(() => import('@/pages/AndroidNotificationTest'));
const CustomerComplaints = lazyImport(() => import('@/pages/CustomerComplaints'));
const CustomerComplaintAnalytics = lazyImport(() => import('@/pages/CustomerComplaints/Analytics'));

// Operator Overview
const OperatorOverview = lazyImport(() => import('@/pages/OperatorOverview'));

// Store onboarding
const GettingStarted = lazyImport(() => import('@/pages/GettingStarted'));
const SetupChecklist = lazyImport(() => import('@/pages/Setup'));
const AdminSetupRequests = lazyImport(() => import('@/pages/Admin/SetupRequests'));

// Store Calendar (accessible from main nav)
const StoreCalendar = lazyImport(() => import('@/pages/Calendar'));


interface PrivateRouteProps {
  children: React.ReactNode;
  requiredFeature?: 'fohTasks' | 'setups' | 'kitchen' | 'documentation' | 'training' | 'evaluations' | 'leadership';
  requiresFullAccess?: boolean;
  minLevel?: number; // Minimum position level required (1-10)
}

function PrivateRoute({ children, requiredFeature, requiresFullAccess, minLevel }: PrivateRouteProps) {
  // Use our new ProtectedRoute component
  return <ProtectedRoute requiredFeature={requiredFeature} requiresFullAccess={requiresFullAccess} minLevel={minLevel}>{children}</ProtectedRoute>;
}

function LegacyEvaluationRouteRedirect() {
  const { id } = useParams<{ id: string }>()

  if (!id) {
    return <Navigate to="/evaluations" replace />
  }

  return <Navigate to={`/evaluations/${id}`} replace />
}

export const publicRoutes = [
  {
    path: '/',
    element: <LandingPage />
  },
  {
    path: '/login',
    element: <Login />
  },
  {
    path: '/register',
    element: <Register />
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />
  },
  {
    path: '/reset-password',
    element: <ResetPassword />
  }
];

export default function AppRoutes() {
  const { user } = useAuth();

  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public Routes */}
      <Route
        path="/"
        element={
          user ? (
            <HomePageRedirect />
          ) : (
            <LandingPage />
          )
        }
      />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* ... rest of the public routes ... */}

      {/* Protected Routes */}
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      <Route path="/foh" element={<PrivateRoute requiredFeature="fohTasks"><FOHLayout /></PrivateRoute>}>
        <Route index element={<FOH />} />
        <Route path="history" element={<FOHHistory />} />
        <Route path="cleaning" element={<CleaningPage />} />
        <Route path="cleaning/history" element={<CleaningHistory />} />
        <Route path="equipment" element={<EquipmentPageGate area="foh"><FohEquipmentPage /></EquipmentPageGate>} />
        {/* Director Board — Directors+ only, and only once the store switches it on */}
        <Route path="directors" element={<DirectorBoardGate><DirectorBoardPage /></DirectorBoardGate>} />
        <Route path="directors/history" element={<DirectorBoardGate><DirectorHistory /></DirectorBoardGate>} />
        {/* FOH Food Safety — same pages as /kitchen/food-safety, area derived from URL */}
        <Route path="food-safety" element={<FohFoodSafetyGate><FoodSafety /></FohFoodSafetyGate>} />
        <Route path="food-safety/complete/:id" element={<FohFoodSafetyGate><CompleteChecklist /></FohFoodSafetyGate>} />
        <Route path="food-safety/daily" element={<FohFoodSafetyGate><DailyChecklist /></FohFoodSafetyGate>} />
        <Route path="food-safety/history" element={<FohFoodSafetyGate><History /></FohFoodSafetyGate>} />
        <Route path="food-safety/history/:id" element={<FohFoodSafetyGate><ChecklistHistory /></FohFoodSafetyGate>} />
        <Route path="food-safety/pages/history" element={<FohFoodSafetyGate><DailyChecklistHistory /></FohFoodSafetyGate>} />
        <Route path="food-safety/pages/temperature-history" element={<FohFoodSafetyGate><TemperatureHistory /></FohFoodSafetyGate>} />
        <Route path="food-safety/analytics/:id" element={<FohFoodSafetyGate><ChecklistAnalytics /></FohFoodSafetyGate>} />
        <Route path="food-safety/view/:id" element={<FohFoodSafetyGate><ViewCompletion /></FohFoodSafetyGate>} />
      </Route>
      <Route path="/setup-sheet-templates" element={<PrivateRoute requiredFeature="setups"><SetupSheetTemplates /></PrivateRoute>} />
      <Route path="/setup-sheet-builder" element={<PrivateRoute requiredFeature="setups"><SetupSheetBuilder /></PrivateRoute>} />
      <Route path="/edit-template/:id" element={<PrivateRoute requiredFeature="setups"><EditTemplate /></PrivateRoute>} />
      <Route path="/saved-setups" element={<PrivateRoute requiredFeature="setups"><SavedSetups /></PrivateRoute>} />
      <Route path="/shift-summary" element={<PrivateRoute requiredFeature="setups" minLevel={3}><ShiftSummary /></PrivateRoute>} />
      <Route path="/shift-summary/history" element={<PrivateRoute requiredFeature="setups" minLevel={3}><ShiftSummaryHistory /></PrivateRoute>} />
      <Route path="/setup-view/:setupId" element={<PrivateRoute requiredFeature="setups"><SetupViewLoader /></PrivateRoute>} />
      <Route path="/setup-view/:setupId/test" element={<PrivateRoute requiredFeature="setups"><SetupTestView /></PrivateRoute>} />
      <Route path="/setup-view/:setupId/print" element={<PrivateRoute requiredFeature="setups"><PrintableSetupView /></PrivateRoute>} />
      <Route path="/evaluations" element={<PrivateRoute requiredFeature="evaluations" minLevel={3}><Evaluations /></PrivateRoute>} />
      <Route path="/evaluations/scheduled" element={<PrivateRoute requiredFeature="evaluations" minLevel={3}><ScheduledEvaluations /></PrivateRoute>} />
      <Route path="/evaluations/new" element={<PrivateRoute requiredFeature="evaluations" minLevel={3}><NewEvaluation /></PrivateRoute>} />
      {/* Leader reviews (multi-reviewer stores). No minLevel: the store picks the reviewer level, and the API only returns a person's own review rows. */}
      <Route path="/evaluations/reviews" element={<PrivateRoute requiredFeature="evaluations"><LeaderReviewsToDo /></PrivateRoute>} />
      <Route path="/evaluations/:id/leader-review" element={<PrivateRoute requiredFeature="evaluations"><LeaderReview /></PrivateRoute>} />
      <Route path="/evaluations/:id/review" element={<PrivateRoute requiredFeature="evaluations"><LegacyEvaluationRouteRedirect /></PrivateRoute>} />
      <Route path="/evaluations/:id/acknowledge" element={<PrivateRoute requiredFeature="evaluations"><EnhancedEvaluationForm /></PrivateRoute>} />
      <Route path="/evaluations/:id/history" element={<PrivateRoute requiredFeature="evaluations"><LegacyEvaluationRouteRedirect /></PrivateRoute>} />
      <Route path="/evaluations/:id/list" element={<PrivateRoute requiredFeature="evaluations"><LegacyEvaluationRouteRedirect /></PrivateRoute>} />
      {/* ViewEvaluation allows any level - employees need access to complete self-evaluations */}
      <Route path="/evaluations/:id" element={<PrivateRoute requiredFeature="evaluations"><ViewEvaluation /></PrivateRoute>} />


      {/* Self-Assessments — own chrome (not the Kitchen layout); the page itself
          renders the "turned off" state, so no settings gate here. Subscription
          gate matches the Kitchen section. */}
      <Route path="/kitchen/self-assessments" element={<PrivateRoute requiredFeature="kitchen"><SelfAssessmentsPage /></PrivateRoute>} />
      <Route path="/kitchen/self-assessments/report" element={<PrivateRoute requiredFeature="kitchen"><SelfAssessmentReportPage /></PrivateRoute>} />

      <Route path="/kitchen" element={<PrivateRoute requiredFeature="kitchen"><Kitchen /></PrivateRoute>}>
        <Route index element={<KitchenHome />} />
        <Route path="waste-tracker" element={<WasteTracker />} />
        <Route path="waste-tracker/analytics" element={<WasteAnalytics />} />
        <Route path="equipment" element={<EquipmentPageGate area="kitchen"><Equipment /></EquipmentPageGate>} />
        <Route path="equipment/history" element={<EquipmentPageGate area="kitchen"><EquipmentHistory /></EquipmentPageGate>} />
        <Route path="equipment/history/:id" element={<EquipmentPageGate area="kitchen"><EquipmentHistoryDetail /></EquipmentPageGate>} />
        <Route path="food-safety" element={<FoodSafety />} />
        <Route path="food-safety/complete/:id" element={<CompleteChecklist />} />
        <Route path="food-safety/daily" element={<DailyChecklist />} />
        <Route path="food-safety/history" element={<History />} />
        <Route path="food-safety/history/:id" element={<ChecklistHistory />} />
        <Route path="food-safety/pages/history" element={<DailyChecklistHistory />} />
        <Route path="food-safety/pages/temperature-history" element={<TemperatureHistory />} />
        <Route path="food-safety/analytics/:id" element={<ChecklistAnalytics />} />
        <Route path="food-safety/view/:id" element={<ViewCompletion />} />

        <Route path="cleaning" element={<KitchenCleaning />} />
        <Route path="cleaning/history" element={<KitchenCleaningHistory />} />

        <Route path="checklists" element={<Checklists />} />
        <Route path="checklists/history" element={<KitchenChecklistHistory />} />
      </Route>

      <Route path="/food-safety" element={<PrivateRoute><FoodSafety /></PrivateRoute>} />

      <Route path="/templates" element={<PrivateRoute><Templates /></PrivateRoute>} />
      <Route path="/templates/new" element={<PrivateRoute><TemplateBuilder /></PrivateRoute>} />
      <Route path="/templates/:id/edit" element={<PrivateRoute><TemplateBuilder /></PrivateRoute>} />
      <Route path="/messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
      <Route path="/calendar" element={<PrivateRoute requiredFeature="leadership"><StoreCalendar /></PrivateRoute>} />
      <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
      <Route path="/android-notification-test" element={<PrivateRoute><AndroidNotificationTest /></PrivateRoute>} />
      <Route path="/vendors" element={<PrivateRoute requiresFullAccess><Vendors /></PrivateRoute>} />
      <Route path="/safe-counting" element={<PrivateRoute minLevel={3}><SafeCounting /></PrivateRoute>} />
      <Route path="/rewards" element={<PrivateRoute><Rewards /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="/users" element={<PrivateRoute requiresFullAccess><Users /></PrivateRoute>} />
      <Route path="/users/assign-managers" element={<PrivateRoute requiresFullAccess><AssignManagers /></PrivateRoute>} />
      <Route path="/users/:id" element={<PrivateRoute><UserProfile /></PrivateRoute>} />
      <Route path="/users/:id/edit" element={<PrivateRoute requiresFullAccess><EditUser /></PrivateRoute>} />
      {/* Redirects for old disciplinary routes */}
      <Route path="/disciplinary" element={<Navigate to="/documentation" replace />} />
      <Route path="/disciplinary/new" element={<Navigate to="/documentation/new" replace />} />
      <Route path="/disciplinary/:id" element={<Navigate to="/documentation" replace />} />
      {/* Documentation - Team members can view their own, Leaders+ can view/create all */}
      <Route path="/documentation" element={<PrivateRoute requiredFeature="documentation"><DocumentationPage /></PrivateRoute>} />
      <Route path="/documentation/analytics" element={<PrivateRoute requiredFeature="documentation" minLevel={3}><DisciplineAnalytics /></PrivateRoute>} />
      <Route path="/documentation/new" element={<PrivateRoute requiredFeature="documentation" minLevel={3}><NewDocument /></PrivateRoute>} />
      <Route path="/documentation/:id" element={<PrivateRoute requiredFeature="documentation"><DocumentDetail /></PrivateRoute>} />
      <Route path="/documentation/:id/edit" element={<PrivateRoute requiredFeature="documentation" minLevel={3}><EditDocument /></PrivateRoute>} />
      <Route path="/analytics" element={<PrivateRoute requiresFullAccess><AnalyticsHub /></PrivateRoute>}>
        <Route path="hearts-and-hands" element={<HeartsAndHands />} />
        <Route path="team-scores" element={<TeamScores />} />
        <Route path="day-vs-night" element={<DayVsNight />} />
        <Route path="evaluation-trends" element={<EvaluationTrends />} />
        <Route path="department-comparison" element={<DepartmentComparison />} />
        <Route path="performance-heatmap" element={<PerformanceHeatmap />} />
      </Route>
      <Route path="/training" element={<PrivateRoute requiredFeature="training"><Training /></PrivateRoute>}>
        <Route index element={<Navigate to="/training/progress" replace />} />
        <Route path="progress" element={<TrainingProgress />} />
        <Route path="progress/:id" element={<TrainingDetails />} />
        <Route path="plans" element={<TrainingPlanList />} />
        <Route path="plans/:id" element={<PlanDetails />} />
        <Route path="community-plans" element={<CommunityPlans />} />
        <Route path="community-plans/:id" element={<CommunityPlanDetails />} />
        <Route path="new-hires" element={<NewHires />} />
        <Route path="assessments" element={<TrainingAssessments />} />
        <Route path="assessments/create" element={<CreateAssessment />} />
        <Route path="assessments/:id/edit" element={<CreateAssessment />} />
        <Route path="assessments/:id/preview" element={<AssessmentPreview />} />
        <Route path="assessments/:id/send" element={<SendAssessment />} />
        <Route path="assessments/:id/conduct" element={<ConductAssessment />} />
        <Route path="assessments/:id/review" element={<ReviewAssessment />} />
        <Route path="assessments/:id/take" element={<TakeAssessment />} />
        <Route path="development-plans/new" element={<DevelopmentPlanBuilder />} />
        <Route path="development-plans/:id/edit" element={<DevelopmentPlanBuilder />} />
        <Route path="development-plans/templates/:id/edit" element={<DevelopmentPlanBuilder mode="template" />} />
        <Route path="assessments/:id/results" element={<TrainingAssessmentResults />} />
        <Route path="*" element={<Navigate to="/training/progress" replace />} />
      </Route>
      <Route path="/future" element={<PrivateRoute><FuturePage /></PrivateRoute>} />

      <Route path="/leadership" element={<PrivateRoute requiredFeature="leadership"><Leadership /></PrivateRoute>}>
        <Route index element={<Navigate to="/leadership/dashboard" replace />} />
        <Route path="dashboard" element={<LeadershipDashboard />} />
        <Route path="development" element={<DevelopmentPlansGuard><DevelopmentPage /></DevelopmentPlansGuard>} />
        <Route path="development-plans" element={<DevelopmentPlansGuard><DevelopmentPage /></DevelopmentPlansGuard>} />
        {/* Legacy routes for backward compatibility */}
        <Route path="developmental-plan" element={<Navigate to="/leadership/development-plans?tab=available" replace />} />
        <Route path="my-plans" element={<Navigate to="/leadership/development-plans?tab=enrolled" replace />} />
        <Route path="plans/:planId/tasks" element={<DevelopmentPlansGuard><PlanTasks /></DevelopmentPlansGuard>} />
        <Route path="personal-plans/:id" element={<PersonalPlanPage />} />
        <Route path="training-programs" element={<TrainingPrograms />} />
        <Route path="training-programs/:id" element={<TrainingProgramDetails />} />
        {/* Leadership characteristics assessment was removed as a product
            ability — old /leadership/characteristics* URLs fall through to
            the catch-all redirect to /leadership/dashboard below. */}
        <Route path="assessments" element={<Assessments />} />
        <Route path="assessments/:assessmentId/take" element={<AssessmentTaking />} />
        <Route path="assessments/:assessmentId/results" element={<AssessmentResults />} />
        <Route path="assessments/:assessmentId/leadership-style-results" element={<LeadershipStyleResults />} />
        <Route path="assessments/:id" element={<AssessmentDetails />} />
        <Route path="360-evaluations" element={<Evaluations360 />} />
        <Route path="360-evaluations/new" element={<New360Evaluation />} />
        <Route path="360-evaluations/:evaluationId" element={<View360Evaluation />} />
        {/* Aliases for the deep links used in 360 invite/reminder emails
            (…/feedback, …/summary, …/results). Without these the email CTA
            hit an unmatched route and evaluators couldn't reach the form. */}
        <Route path="360-evaluations/:evaluationId/feedback" element={<View360Evaluation />} />
        <Route path="360-evaluations/:evaluationId/summary" element={<View360Evaluation />} />
        <Route path="360-evaluations/:evaluationId/results" element={<View360Evaluation />} />
        <Route path="360-evaluations/:evaluationId/evaluators" element={<Evaluators />} />
        <Route path="360-evaluations/:evaluationId/evaluators/add" element={<AddEvaluators />} />
        <Route path="360-evaluations/:evaluationId/development-plan" element={<DevelopmentPlan />} />
        <Route path="leadership-development" element={<LeadershipDevelopment />} />
        {/* The old roadmap viewer/builder is retired — areas live on the
            leadership dashboard now. Old roadmap URLs (roadmap-builder/*,
            roadmaps/*, area-roadmaps/*) fall through to the catch-all
            redirect to /leadership/dashboard below. */}
        <Route path="settings" element={<LeadershipSettings />} />
        <Route path="my-leaders" element={<MyLeaders />} />
        <Route path="one-on-ones" element={<OneOnOnePage />} />
        <Route path="calendar" element={<LeadershipCalendar />} />
        <Route path="subscription" element={<Subscription />} />
        <Route path="*" element={<Navigate to="/leadership/dashboard" replace />} />
      </Route>


      <Route path="/team-surveys" element={<PrivateRoute requiredFeature="leadership" requiresFullAccess><TeamSurveys /></PrivateRoute>}>
        <Route index element={<AllTeamSurveys />} />
        <Route path="dashboard" element={<TeamSurveysDashboard />} />
        <Route path="analytics" element={<TeamSurveyAnalytics />} />
        <Route path="new" element={<NewTeamSurvey />} />
        <Route path="create-advanced" element={<CreateAdvanced />} />
        <Route path="templates" element={<SurveyTemplates />} />
        <Route path=":surveyId/edit" element={<TeamSurveyBuilder />} />
        <Route path=":surveyId/results" element={<TeamSurveyResults />} />
      </Route>

      {/* Anonymous survey taking route (no auth required) */}
      <Route path="/survey/:token" element={<TakeSurvey />} />

      <Route path="/team-development" element={<PrivateRoute requiredFeature="leadership"><TeamDevelopment /></PrivateRoute>}>
        <Route index element={<TeamDevelopmentRedirect />} />
        <Route path="my-plans" element={<TeamMyPlans />} />
        <Route path="overview" element={<TeamOverview />} />
      </Route>

      {/* Shifts functionality removed */}

      {/* Chat */}
      <Route path="/chat" element={<PrivateRoute><Chat /></PrivateRoute>} />

      {/* Customer Complaints / Guest Recovery */}
      <Route path="/customer-complaints" element={<PrivateRoute><CustomerComplaints /></PrivateRoute>} />
      <Route path="/customer-complaints/analytics" element={<PrivateRoute><CustomerComplaintAnalytics /></PrivateRoute>} />

      {/* Operator Overview - Permission checked in component */}
      <Route path="/operator-overview" element={<PrivateRoute><OperatorOverview /></PrivateRoute>} />

      {/* Help and Documentation */}
      <Route path="/how-to" element={<PrivateRoute><HowTo /></PrivateRoute>} />

      {/* Quick Links */}
      <Route path="/quick-links" element={<PrivateRoute><QuickLinks /></PrivateRoute>} />

      {/* Borrow Log — the page itself renders the "turned off" state, so the
          route is not gated here. */}
      <Route path="/borrow-log" element={<PrivateRoute><BorrowLogPage /></PrivateRoute>} />

      {/* Shift Reports add-on — no feature/level gate on purpose: any position with a
          question set can file (incl. level-2 trainers). The page routes by /me. */}
      <Route path="/shift-reports" element={<PrivateRoute><ShiftReportsPage /></PrivateRoute>} />
      <Route path="/shift-reports/new" element={<PrivateRoute><ShiftReportsPage view="form" /></PrivateRoute>} />

      {/* Late time-off requests — same: the page renders its own "turned off" state. */}
      <Route path="/time-off" element={<PrivateRoute><LateTimeOffPage /></PrivateRoute>} />

      {/* ServSafe add-on — same: the page renders its own "turned off" state and
          splits team member / admin by /me. */}
      <Route path="/servsafe" element={<PrivateRoute><ServSafePage /></PrivateRoute>} />
      <Route path="/servsafe/inspector" element={<PrivateRoute><ServSafeInspectorPage /></PrivateRoute>} />

      {/* Group meetings — auth only; read/edit rights are enforced per-meeting
          by the server (creator/attendee/admin), not by a feature gate. */}
      <Route path="/meetings" element={<PrivateRoute><MeetingsList /></PrivateRoute>} />
      <Route path="/meetings/new" element={<PrivateRoute><NewMeeting /></PrivateRoute>} />
      <Route path="/meetings/:id" element={<PrivateRoute><MeetingDetail /></PrivateRoute>} />
      <Route path="/getting-started" element={<PrivateRoute><GettingStarted /></PrivateRoute>} />
      {/* Not admin-gated: a leader handed a section sees their slice here. */}
      <Route path="/setup" element={<PrivateRoute><SetupChecklist /></PrivateRoute>} />

      {/* Admin Pages - Restricted Access (Main Admin Only) */}
      <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="stores" element={<StoresPage />} />
        <Route path="stores/:storeId/subscription" element={<SubscriptionManagementPage />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="health" element={<HealthDashboardPage />} />
        <Route path="prospects" element={<ProspectsPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="outreach" element={<OutreachPage />} />
        <Route path="first-login" element={<FirstLoginPage />} />
        <Route path="setup-requests" element={<AdminSetupRequests />} />
      </Route>

      <Route path="/admin/rewards" element={<PrivateRoute requiresFullAccess><RewardsManagement /></PrivateRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

// client/src/components/Layout.tsx
